import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { axiosInstance } from '../config';
import './PremiumCheckout.css';

const PLAN_INFO = {
  monthly: { name: 'Premium Monthly', price: 60,  period: 'month', duration: '1 month'   },
  yearly:  { name: 'Premium Yearly',  price: 899, period: 'year',  duration: '12 months' },
};

export default function PremiumCheckout() {
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'monthly';
  const plan = PLAN_INFO[planId] || PLAN_INFO.monthly;

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);

  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidating, setDiscountValidating] = useState(false);
  const [discountInfo, setDiscountInfo] = useState(null); // { code, discountPercent, message }
  const [discountError, setDiscountError] = useState('');

  const discountedPrice = discountInfo
    ? (plan.price * (1 - discountInfo.discountPercent / 100)).toFixed(2)
    : plan.price.toFixed(2);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userId = localStorage.getItem('userID');
        if (!userId) { setError('Please log in to subscribe.'); return; }
        const res = await axiosInstance.get(`/users/profile/${userId}`);
        if (res.data && res.data.user) setUser(res.data.user);
      } catch {
        setError('Could not load your profile. Please log in again.');
      }
    };
    loadUser();
  }, []);

  const handleValidateDiscount = async () => {
    if (!discountCode.trim()) return;
    setDiscountError('');
    setDiscountInfo(null);
    setDiscountValidating(true);
    try {
      const res = await axiosInstance.post('/premium/validate-discount', { code: discountCode.trim() });
      if (res.data.valid) setDiscountInfo(res.data);
    } catch (err) {
      setDiscountError(err.response?.data?.message || 'Invalid discount code.');
    } finally {
      setDiscountValidating(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountCode('');
    setDiscountInfo(null);
    setDiscountError('');
  };

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    setError('');
    if (!acceptedTerms) { setError('You must accept the Terms and Conditions to proceed.'); return; }
    if (!user) { setError('Please log in to subscribe.'); return; }

    setLoading(true);
    try {
      const baseUrl = window.location.origin;
      const res = await axiosInstance.post('/premium/initiate-payment', {
        planId,
        returnUrl:    `${baseUrl}/premium/success`,
        cancelUrl:    `${baseUrl}/premium`,
        nameFirst:    (user.firstName || '').trim() || 'Customer',
        nameLast:     (user.lastName  || '').trim() || 'User',
        emailAddress: user.email || '',
        discountCode: discountInfo ? discountInfo.code : undefined
      });

      if (res.data?.paymentUrl && res.data?.formData) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = res.data.paymentUrl;
        form.style.display = 'none';
        Object.entries(res.data.formData).forEach(([key, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        return;
      }
      setError(res.data?.message || 'Could not start payment.');
    } catch (err) {
      setError(err.response?.data?.message || 'Payment could not be started. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="premium-checkout-page">
      <div className="premium-checkout-card">
        <Link to="/premium" className="premium-checkout-back">Back to plans</Link>
        <h1>Complete your subscription</h1>

        <div className="premium-checkout-summary">
          <p><strong>{plan.name}</strong></p>
          {discountInfo ? (
            <>
              <p className="premium-checkout-original-price">R {plan.price.toFixed(2)} / {plan.period}</p>
              <p className="premium-checkout-price">
                R {discountedPrice} / {plan.period}
                <span className="premium-checkout-discount-badge">{discountInfo.discountPercent}% OFF</span>
              </p>
            </>
          ) : (
            <p className="premium-checkout-price">R {plan.price.toFixed(2)} / {plan.period}</p>
          )}
        </div>

        <p className="premium-checkout-info">
          This is a <strong>one-time payment</strong> for {plan.duration} of premium access. There
          is no automatic renewal — when your access expires you can choose to pay again. You will
          be redirected to PayFast to complete payment securely. Card, EFT, SnapScan and more are
          supported.
        </p>

        {/* Discount code block */}
        <div className="premium-discount-section">
          <label className="premium-discount-label">Have a discount code?</label>
          {discountInfo ? (
            <div className="premium-discount-applied">
              <span className="premium-discount-success">✓ {discountInfo.message}</span>
              <button type="button" className="premium-discount-remove" onClick={handleRemoveDiscount}>
                Remove
              </button>
            </div>
          ) : (
            <div className="premium-discount-row">
              <input
                type="text"
                className="premium-discount-input"
                placeholder="Enter code"
                value={discountCode}
                onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountError(''); }}
              />
              <button
                type="button"
                className="premium-discount-btn"
                onClick={handleValidateDiscount}
                disabled={discountValidating || !discountCode.trim()}
              >
                {discountValidating ? '...' : 'Apply'}
              </button>
            </div>
          )}
          {discountError && <p className="premium-checkout-error" style={{ marginTop: '0.4rem' }}>{discountError}</p>}
        </div>

        <form onSubmit={handleProceedToPayment} className="premium-checkout-form">
          <label className="premium-checkout-terms">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <span>
              I accept the{' '}
              <Link to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</Link>
              {' '}and the{' '}
              <Link to="/premium/terms" target="_blank" rel="noopener noreferrer">Premium Subscription Terms</Link>.
            </span>
          </label>

          {error && <p className="premium-checkout-error">{error}</p>}

          <button
            type="submit"
            className="premium-checkout-submit"
            disabled={loading || !acceptedTerms}
          >
              {loading ? 'Redirecting to PayFast...' : `Pay R ${discountedPrice} via PayFast`}
          </button>
        </form>
      </div>
    </div>
  );
}
